import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { VisitorType } from '@app/models/visitortype.model';
import { LocalizationService } from '../internationalization/localization.service';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Brand } from '@app/models/brand.model';
import { Domain } from "@app/models/domain.model";
import { VisitReason } from '@app/models/visitreason.model';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ReasonForVisitDeleteComponent } from './reason-for-visit-delete/reason-for-visit-delete.component';
import { ReasonForVisitAddEditComponent } from './reason-for-visit-add-edit/reason-for-visit-add-edit.component';
import { ReferenceService } from '@app/services/reference/reference.service';
import { debounceTime, distinctUntilChanged, race } from 'rxjs';
import { NavigationService } from '@app/services/navigation/navigation.service';
import { MatSort, Sort } from '@angular/material/sort';
import { Language } from '@app/models/language.model';
import { ConfigurationService } from '@app/services/configuration/configuration.service';
import { environment } from '@env/environment';
import { Translation } from '@app/models/translation.model';
import { VisitorTypeUploadReason } from '@app/models/visitortypeuploadreason.model';

@Component({
    selector: 'app-visitor-type-management-add-edit',
    templateUrl: './visitor-type-management-add-edit.component.html',
    styleUrls: ['./visitor-type-management-add-edit.component.scss'],
    standalone: false
})
export class VisitorTypeManagementAddEditComponent implements OnInit, AfterViewInit {
  readonly REASON_VISIT: string = 'ReasonVisit';
  readonly UPLOAD_REASON: string = 'UploadReason';

  public formGroup!: UntypedFormGroup;
  allVisitorTypes: VisitorType[] = [];
  visitorTypeUploadReasons: VisitorTypeUploadReason[] = [];
  visitorType: VisitorType | undefined;
  existingBrands: Brand[] = [];
  existingDomains: Domain[] = [];
  searchControl: FormControl = new FormControl('');
  debounce: number = 1000;
  isEditMode: boolean = false;
  isVisitorTypeCreated: boolean = false;

  originalVisitReasons: VisitReason[] = [];
  displayedReasonForVisitColumns: string[] = ['Description', 'IsActive', 'Actions' ];
  displayedUploadReasonColumns: string[] = ['Description', 'IsActive', 'Actions'];

  editToolTip: string;
  deleteToolTip: string;
  dataSource = new MatTableDataSource<VisitReason>(this.originalVisitReasons);
  @ViewChild(MatPaginator) paginator: MatPaginator = new MatPaginator(new MatPaginatorIntl(), ChangeDetectorRef.prototype);
  @ViewChild(MatSort) sort: MatSort;
  pageSize: number = 10;
  totalItems: number = 0;

  uploadReasonSearchControl: FormControl = new FormControl('');
  uploadReasonDataSource = new MatTableDataSource<VisitorTypeUploadReason>(this.visitorTypeUploadReasons);
  @ViewChild("uploadReasonPaginator") uploadReasonPaginator: MatPaginator = new MatPaginator(new MatPaginatorIntl(), ChangeDetectorRef.prototype);
  @ViewChild(MatSort) uploadReasonSort: MatSort;
  uploadReasonPageSize: number = 10;
  uploadReasonTotalItems: number = 0;
  selectedUploadReasonIsActive: boolean = true;
  selectedUploadReasonSearchValue: string = '';

  defaultSelectDomainOption: Domain = { 
    id: this._localizationService.translate("visitor_type_management_add_edit_default_domain_selection_id"), 
    name: this._localizationService.translate("visitor_type_management_add_edit_default_domain_selection_name"), 
    logoutURL: '' };

  originalFormGroup: any = {
    typeDescription: '',
    typeTranslations:[],
    typeBrand: '',
    typeDomain: '',
    typeInactive: false
  }

  selectedBrandName: string | undefined = '';
  selectedDomainName: string | undefined = '';
  selectedVisitorTypeId: string | null;
  selectedIsActive: boolean = true;
  selectedSearchValue: string = '';
  component: { value: null; };
  isMutliLanguageEnable: boolean = false;
  languages: Language[] = [];

  constructor(private _route: ActivatedRoute,
    private _router: Router,
    private _localizationService: LocalizationService,
    private _formBuilder: UntypedFormBuilder,
    private _addFileDialog: MatDialog,
    private _referenceService: ReferenceService,
    private _navigationService: NavigationService,
    private _configurationService: ConfigurationService
  ) {
    this._navigationService.onChange(true, false);
    this.formGroup = this.initFormGroup();
    this.editToolTip = this._localizationService.translate("visitor_type_management_add_edit_edit_tooltip");
    this.deleteToolTip = this._localizationService.translate("visitor_type_management_add_edit_delete_tooltip");
  }

  async ngOnInit(): Promise<void> {
   
    await this.loadLanguages();
    this._route.paramMap.subscribe(async params => {
      this.selectedVisitorTypeId = params.get('visitorTypeId');
   
      if(this.selectedVisitorTypeId && this.selectedVisitorTypeId !== ''){
        await this.loadVisitorType();
        this.closeSearch(this.REASON_VISIT);
        this.searchValues(this.REASON_VISIT);

        await this.loadUploadReasons();
        this.closeSearch(this.UPLOAD_REASON);
        this.searchValues(this.UPLOAD_REASON);
      }else{
        await this.loadDomains();
        await this.loadAllVisitorTypes();
        await this.loadBrands();
      }
    
     });
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    this.uploadReasonDataSource.paginator = this.uploadReasonPaginator;
    this.uploadReasonDataSource.sort = this.uploadReasonSort;
  }

  private initFormGroup(): UntypedFormGroup {
    const formGroup = this._formBuilder.group({
      description: ['', [Validators.required, Validators.maxLength(250)]],
      brand: ['', Validators.required],
      domain: [''],
      isActive: [true]
    });
    return formGroup;
  }

  async loadVisitorType(): Promise<void> {

      this._referenceService.getVisitorTypeById(this.selectedVisitorTypeId).subscribe({
        next: async (visitorType) => {
          if (visitorType) {
            this.visitorType = visitorType;
            if(this.visitorType.translations === null){
              this.visitorType.translations = [];
            }
            this.isEditMode = true;

            if (visitorType.brand) {
              this.selectedBrandName = visitorType.brand?.name;
              this.existingBrands.push(visitorType.brand!);
            }

            if (visitorType.domain) {
              this.selectedDomainName = visitorType.domain?.name;
              this.existingDomains.push(visitorType.domain!);
            }
            await this.populateFormWithVisitorType(visitorType);
            await this.loadVisitReasons();
          }
        },
        error: (error) => {
          if (error.error && error.error.errorCode) {
            alert(this._localizationService.translate(error.error.errorCode));
          }
          else {
            alert(this._localizationService.translate("visitor_type_management_add_edit_error_fetching_visitor_type_by_id"));
          }
          console.error("visitor_type_management_add_edit_error_fetching_visitor_type_by_id", error);
        }
      });
  }

  async loadUploadReasons() {
    this._referenceService.getUploadReasonsByVisitorTypeId(this.selectedVisitorTypeId!, true).subscribe({
      next: (visitorTypeUploadReasons) => {
        if (visitorTypeUploadReasons !== null) {
          this.visitorTypeUploadReasons = visitorTypeUploadReasons;
          this.uploadReasonDataSource.data = visitorTypeUploadReasons;
          this.uploadReasonDataSource.paginator = this.uploadReasonPaginator;
          this.uploadReasonDataSource.sort = this.uploadReasonSort;
          this.onFilterChange(this.UPLOAD_REASON);
        }
      },
      error: (error) => {
        console.error(this._localizationService.translate("visitor_type_management_error_fetching_visitor_types"), error);
      }
    });
  }

  async loadLanguages() {
    this.isMutliLanguageEnable = await this._configurationService.getIsMultiLanguageEnable();
    if(this.isMutliLanguageEnable){
      this._localizationService.getLanguages().subscribe({
        next: async (langs) => {
          this.languages = [...langs.filter(lang => lang.code !== environment.defaultLang)];

          if(this.languages.length > 0){
            this.languages.forEach(lang => {
                const langCode = lang.code.replace('-', '_');
                this.formGroup.addControl(langCode, this._formBuilder.control('', [Validators.maxLength(250)]));
            });
          }
        }
      });
    }
  }

  async loadAllVisitorTypes() {
    this._referenceService.getAllVisitorTypes(true).subscribe({
      next: (visitorTypes) => {
        if (visitorTypes !== null && visitorTypes.length > 0) {
          this.allVisitorTypes = visitorTypes;
        }
      },
      error: (error) => {
        console.error(this._localizationService.translate("visitor_type_management_add_edit_error_fetching_visitor_types"), error);
      }
    });
  }

  async populateFormWithVisitorType(vt: VisitorType) {
    if (vt) {
      this.formGroup.patchValue({
        description: vt.description,
        brand: vt.brand!.name,
        domain: vt.domain?.name,
        isActive: vt.isActive
      });

      if(this.languages.length > 0){
        this.languages.forEach(lang => {
          let langCode = lang.code.replace('-', '_');
          var translatedValue = vt.translations?.find(s => s.languageCode == lang.code)?.description ? vt.translations?.find(s => s.languageCode == lang.code)?.description : '';

          this.formGroup.patchValue({ [langCode]: translatedValue });
          this.originalFormGroup.typeTranslations.push({ langCode: langCode, description: translatedValue });
        });
      }

      this.originalFormGroup.typeDescription = vt.description;
      this.originalFormGroup.typeBrand = vt.brand!.name;
      this.originalFormGroup.typeDomain = vt.domain?.name;
      this.originalFormGroup.typeInactive = vt.isActive;

      this.selectedBrandName = vt.brand?.name;
      this.selectedDomainName = vt.domain?.name;

      this.formGroup.get('brand')?.disable();
      this.formGroup.get('domain')?.disable();
    }
  }

  async loadBrands() {
    this._referenceService.getBrands(true).subscribe({
      next: (brands) => {
        if (brands !== null && brands.length > 0) {
          this.existingBrands = brands;
        }
      },
      error: (error) => {
        console.error(this._localizationService.translate("visitor_type_management_add_edit_error_fetching_brands"), error)
      }
    });
  }

  async loadDomains() {
    this._referenceService.getAllDomains().subscribe({
      next: (domains) => {
        if (domains !== null && domains.length > 0) {
          this.existingDomains = [this.defaultSelectDomainOption, ...domains];
        }
      },
      error: (error) => {
        console.error(this._localizationService.translate("visitor_type_management_add_edit_error_fetching_domains"), error);
      }
    });
  }

  async loadVisitReasons() {
    this._referenceService.getVisitReasonsByVisitorTypeId(this.selectedVisitorTypeId!, true).subscribe({
      next: (visitReasons) => {
        if (visitReasons !== null) {
          this.originalVisitReasons = visitReasons;
          this.dataSource.data = visitReasons;
          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;
          this.onFilterChange(this.REASON_VISIT);
        }
      },
      error: (error) => {
        if (error.error && error.error.errorCode) {
          alert(this._localizationService.translate(error.error.errorCode));
        }
        else {
          alert(this._localizationService.translate("visitor_type_management_add_edit_error_fetching_visit_reasons"));
        }

        console.error(this._localizationService.translate("visitor_type_management_add_edit_error_fetching_visit_reasons"), error);
      }
    });
  }

  searchValues(type: string) {
    if(type === this.REASON_VISIT)
    {
      this.searchControl.valueChanges
        .pipe(debounceTime(this.debounce), distinctUntilChanged())
        .subscribe(query => {
          if (query && query.length > 2) {
            this.selectedSearchValue = query.toLowerCase();
          } else {
          this.selectedSearchValue = '';
          }

          this.onFilterChange(this.REASON_VISIT);
        });
    } else if(type === this.UPLOAD_REASON){
      this.uploadReasonSearchControl.valueChanges
        .pipe(debounceTime(this.debounce), distinctUntilChanged())
        .subscribe(query => {

          if(query && query.length > 2){
            this.selectedUploadReasonSearchValue = query.toLowerCase();
          }else{
            this.selectedUploadReasonSearchValue = '';
          }
          this.onFilterChange(this.UPLOAD_REASON);
      });
    }
  }

  onFilterChange(type: string){
    if(type === this.UPLOAD_REASON){
      this.uploadReasonDataSource.data = this.visitorTypeUploadReasons.filter(vt =>
        vt.description.toLocaleLowerCase().includes(this.selectedUploadReasonSearchValue) &&
        vt.isActive == this.selectedUploadReasonIsActive
      );
    } else if (type === this.REASON_VISIT){
      this.dataSource.data = this.originalVisitReasons.filter(vt =>
        vt.description.toLocaleLowerCase().includes(this.selectedSearchValue) &&
        vt.isActive == this.selectedIsActive
      );
    }
  }

  saveOnClick(): void {
    if (this.formGroup.valid) {

      if (this.isEditMode && this.visitorType) {

        this.languages.forEach(lang => {
          const langCode = lang.code.replace('-', '_');

          let visitorTypeTranslation = this.visitorType?.translations?.find(t => t.languageCode === lang.code);
          if(visitorTypeTranslation){
            visitorTypeTranslation.description = this.formGroup.get(langCode)?.value;
          }else{
            const newTransValue = this.formGroup.get(langCode)?.value;
            if(newTransValue){
              let newTranslation : Translation = { languageCode: lang.code, description: newTransValue };
              this.visitorType?.translations?.push(newTranslation);
            }
          }
        });

        const updatedVisitorType: VisitorType = {
          id: this.visitorType.id,
          isActive: this.formGroup.get('isActive')?.value,
          description: this.formGroup.get('description')?.value,
          translations: this.visitorType.translations !== null ? this.visitorType.translations : undefined,
          brand: this.visitorType.brand,
          domain: this.visitorType.domain !== null ? this.visitorType.domain : undefined,
        }
        const isExisted = this.isVisitorTypeExisted(updatedVisitorType);
        if (!isExisted) {
          this.updateVisitorType(updatedVisitorType);
          this.loadAllVisitorTypes();
        }
      } else {

        const brandName = this.formGroup.get('brand')?.value;
        let selectedBrand: Brand | undefined = this.getValidBrandObject(brandName);

        const domainName = this.formGroup.get('domain')?.value;
        let selectedDomain: Domain | undefined;

        if (domainName && domainName !== this.defaultSelectDomainOption.name) {
          selectedDomain = this.getValidDomainObject(domainName);
        }

        let newTranslations: Translation[] = [];
        this.languages.forEach(lang => {
          const langCode = lang.code.replace('-', '_');
          const newTransValue = this.formGroup.get(langCode)?.value;
          if(newTransValue){
            let newTranslation : Translation = { languageCode: lang.code, description: newTransValue };
            newTranslations.push(newTranslation);
          }
        });
        
        const newVisitorType: VisitorType = {
          id: undefined,
          isActive:  this.formGroup.get('isActive')?.value,
          description: this.formGroup.get('description')?.value,
          translations: newTranslations,
          brand: selectedBrand ? selectedBrand : undefined,
          domain: selectedDomain ? selectedDomain : undefined
        }
        const isExisted = this.isVisitorTypeExisted(newVisitorType);
        if (!isExisted) {
          this.addNewVisitorType(newVisitorType);
          this.loadAllVisitorTypes();
        }
      }
    }
  }

  public hasError(form: UntypedFormGroup, controlName: string): boolean {
    const validationOutput = form.controls[controlName].hasError('maxlength');
    return validationOutput;
  }

  disableSaveOnClick(): boolean {
    const currentDescription = this.formGroup.get('description')?.value;
    const currentDomain = this.formGroup.get('domain')?.value;
    const currentBrand = this.formGroup.get('brand')?.value;
    const currentIsActive = this.formGroup.get('isActive')?.value;
    let isTranslationsDescriptionChanged = false;

    if(this.languages.length > 0){
      this.languages.forEach(lang => {
        const langCode = lang.code.replace('-', '_');
        const currentTranslation = this.formGroup.get(langCode)?.value;
        const originalTranslation = this.originalFormGroup.typeTranslations.find((t: any) => t.langCode === langCode)?.description;
        if(currentTranslation !== originalTranslation){
          isTranslationsDescriptionChanged = true;
        }
      });
    }

    if (currentDescription === this.originalFormGroup.typeDescription &&
      currentBrand === this.originalFormGroup.typeBrand &&
      currentDomain === this.originalFormGroup.typeDomain && !isTranslationsDescriptionChanged &&
     currentIsActive === this.originalFormGroup.typeInactive) {
      return true;
    }
    
    return this.formGroup.invalid;
  }

  onSortChange(event: any, type: string) {
    if (!event.active || event.direction === '') {
      return;
    }

    if(type === this.REASON_VISIT) {
    
        this.dataSource.data = this.dataSource.data.slice().sort((a, b) => {
          const isAsc = event.direction === 'asc';
          switch (event.active) {
            case 'Description': return this.compare(a.description || '', b.description || '', isAsc);
            default: return 0;
          }
        });
    }else if(type === this.UPLOAD_REASON) {

      this.uploadReasonDataSource.data = this.uploadReasonDataSource.data.slice().sort((a, b) => {
        const isAsc = event.direction === 'asc';
        switch (event.active) {
          case 'Description': return this.compare(a.description || '', b.description || '', isAsc);
          case 'IsActive': return this.compare(a.isActive || '', b.isActive || '', isAsc);
          default: return 0;
        }
      });
    }

  }

  compare(a: string | number | boolean, b: string | number | boolean, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  onSearchInputKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }

  closeOnClick(): void {
    this._router.navigate(['visitortypemanagement']);
  }

  openSearch(type: string) {
   
    if(type === this.UPLOAD_REASON) {
      this.uploadReasonSearchControl.setValue('');
    } else if(type === this.REASON_VISIT){
      this.searchControl.setValue('');
    }
  };

  closeSearch(type: string) {

    if(type === this.UPLOAD_REASON) {
      this.uploadReasonSearchControl.setValue(null);
      this.selectedUploadReasonSearchValue = '';
    } else if(type === this.REASON_VISIT){ 
      this.searchControl.setValue(null);
    }
  };

  showSearchButton(type: string) {
    if(type === this.UPLOAD_REASON) {
      return this.uploadReasonSearchControl.value === null;
    }else if(type === this.REASON_VISIT){ 
      return this.searchControl.value === null;
    }

    return false;
  };

  showSearchInput(type: string) {

    if(type === this.UPLOAD_REASON) {
      return this.uploadReasonSearchControl.value !== null;
    } else if(type === this.REASON_VISIT){ 
      return this.searchControl.value !== null;
    }

    return false;
  };

  addNewVisitorType(visitorType: VisitorType) {
    this._referenceService.addVisitorType(visitorType).subscribe({
      next: (visitorType) => {
        if (visitorType) {
          this.visitorType = visitorType;
          this.isVisitorTypeCreated = true;
          this.formGroup.disable();
          alert(this._localizationService.translate("visitor_type_management_add_edit_snackbar_visitor_type_added_msg"));
          this._router.navigate(['/visitortypeaddeditmanagement', visitorType.id]);
        }
      },
      error: (error) => {
        if (error.error && error.error.errorCode) {
          alert(this._localizationService.translate(error.error.errorCode));
        }
        else {
          alert(this._localizationService.translate("visitor_type_management_add_edit_error_adding_visitor_types"));
        }
        console.error(this._localizationService.translate("visitor_type_management_add_edit_error_adding_visitor_types"), error);
      }
    })
  }

  updateVisitorType(visitorType: VisitorType) {
    this._referenceService.updateVisitorType(visitorType).subscribe({
      next: async (visitorType) => {
        if (visitorType) {
          alert(this._localizationService.translate("visitor_type_management_add_edit_snackbar_visitor_type_edited_msg"));
          this.originalFormGroup.typeDescription = visitorType.description;
         this.reloadPage();
        }
      },
      error: (error) => {
        if (error.error && error.error.errorCode) {
          alert(this._localizationService.translate(error.error.errorCode));
        }
        else {
          alert(this._localizationService.translate("visitor_type_management_add_edit_error_updating_visitor_types"));
        }
        console.error(this._localizationService.translate("visitor_type_management_add_edit_error_updating_visitor_types"), error);
      }
    })
  }

  addNewUploadReason(): void {
    this._router.navigate(['/uploadreasonsaddeditmanagement'], { queryParams: { id: '', visitortypeid: this.selectedVisitorTypeId } });
  }

  editUploadReason(visitorTypeUploadReason: VisitorTypeUploadReason): void {
    this._router.navigate(['/uploadreasonsaddeditmanagement'], { queryParams: { id: visitorTypeUploadReason.id, visitortypeid: this.selectedVisitorTypeId } });
  }

  reloadPage() {
    window.location.reload();
  }

  isVisitorTypeExisted(visitorType: VisitorType): boolean {

    const isExisted = this.allVisitorTypes.some((vt) => {
      return vt.description.toLowerCase() === visitorType.description.toLowerCase().trim() &&
        vt.brand?.name.toLowerCase() === visitorType.brand?.name.toLowerCase().trim() &&
        vt.domain?.name.toLowerCase() === visitorType.domain?.name.toLowerCase().trim();
    });

    if (isExisted) {
      alert(this._localizationService.translate("visitor_type_management_add_edit_error_visitor_types_already_exist"));
    }

    return isExisted;
  }

  getValidBrandObject(brandName: string): Brand | undefined {
    const foundBrand = this.existingBrands.find(brand => brand && brand.name && brand.name.toLowerCase() === brandName.toLocaleLowerCase());
    return foundBrand ? foundBrand : undefined;
  }

  getValidDomainObject(domainName: string): Domain | undefined {
    const foundDomain = this.existingDomains.find(domain => domain && domain.name && domain.name.toLowerCase() === domainName.toLowerCase());
    return foundDomain ? foundDomain : undefined;
  }

  addNewReasonForVisit() {
    let addEditVisitorType: MatDialogRef<ReasonForVisitAddEditComponent>;
    const dialogConfig = {
      data: {
        reason: undefined,
        type: this.visitorType,
        reasonList: this.originalVisitReasons,
        languages: this.languages
      },
      disableClose: true,
      autoFocus: false,
      maxWidth: '100vw !important',
      maxHeight: '100vw !important',
      panelClass: 'details-container'
    };
    addEditVisitorType = this._addFileDialog.open(ReasonForVisitAddEditComponent, dialogConfig);
    addEditVisitorType.addPanelClass('details-container');
    addEditVisitorType.afterClosed().subscribe((result) => {
      if (result) {
        this.selectedVisitorTypeId = result.visitorTypeId;
        this.loadVisitReasons();
        this.closeSearch(this.REASON_VISIT);
        alert(this._localizationService.translate("visitor_type_management_add_edit_snackbar_visit_reason_added_msg"))
      }
    });
  }

  editReasonForVisit(reason: VisitReason) {
    let addEditVisitorType: MatDialogRef<ReasonForVisitAddEditComponent>;
    const dialogConfig = {
      data: {
        reason: reason,
        type: this.visitorType,
        reasonList: this.originalVisitReasons,
        languages: this.languages
      },
      disableClose: true,
      autoFocus: false,
      maxWidth: '100vw !important',
      maxHeight: '100vw !important',
      panelClass: 'details-container'
    };
    addEditVisitorType = this._addFileDialog.open(ReasonForVisitAddEditComponent, dialogConfig);
    addEditVisitorType.addPanelClass('details-container');
    addEditVisitorType.afterClosed().subscribe((result) => {
      if (result) {
        this.loadVisitReasons();
        this.closeSearch(this.REASON_VISIT);
        alert(this._localizationService.translate("visitor_type_management_add_edit_snackbar_visit_reason_edit_msg"));
      }
    })
  }

  deleteReasonForVisit(reason: VisitReason) {
    let deleteVisitorType: MatDialogRef<ReasonForVisitDeleteComponent>;
    const dialogConfig = {
      data: {
        reason: reason
      },
      disableClose: true,
      autoFocus: false,
      maxWidth: '100vw !important',
      maxHeight: '100vw !important',
      panelClass: 'details-container'
    };
    deleteVisitorType = this._addFileDialog.open(ReasonForVisitDeleteComponent, dialogConfig);
    deleteVisitorType.addPanelClass('details-container');
    deleteVisitorType.afterClosed().subscribe((result) => {
      if (result) {
        this.loadVisitReasons();
        this.closeSearch(this.REASON_VISIT);
        alert(this._localizationService.translate("visitor_type_management_add_edit_snackbar_visit_reason_delete_msg"));
      }
    })
  }

  back() {
    this._router.navigate(['visitortypemanagement']);
  }

}